/** Footer (spec rows 18, 21 and 22): build meta, Send, the progress bar and the inline discard confirm. */
import { SendHorizontal } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { getConfig } from '@/lib/config';

function buildLabel(): string {
  try {
    const { environment, version } = getConfig();
    return `${environment} · build ${version}`;
  } catch {
    // Config not loaded (never in the running app); show nothing misleading.
    return 'build unknown';
  }
}

interface Props {
  sending: boolean;
  progress: number | null;
  canSend: boolean;
  confirmDiscard: boolean;
  onSend: () => void;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

export function PanelFooter({ sending, progress, canSend, confirmDiscard, onSend, onKeepEditing, onDiscard }: Readonly<Props>) {
  return (
    <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
      {sending && (
        <Progress value={progress ?? 0} aria-label="Sending feedback" data-testid="feedback-progress" />
      )}
      {confirmDiscard ? (
        <Alert variant="warning" data-testid="feedback-discard-confirm" className="flex items-center gap-2 py-2">
          <AlertDescription className="mr-auto text-foreground">Discard this draft?</AlertDescription>
          <Button type="button" variant="outline" size="sm" data-testid="feedback-keep-editing" onClick={onKeepEditing}>
            Keep editing
          </Button>
          <Button type="button" variant="destructive" size="sm" data-testid="feedback-discard" onClick={onDiscard}>
            Discard
          </Button>
        </Alert>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span data-testid="feedback-build" className="truncate text-xs text-muted-foreground">
            {buildLabel()}
          </span>
          {canSend && (
            <Button
              type="button"
              size="sm"
              variant="default"
              data-testid="feedback-send"
              disabled={sending}
              aria-disabled={sending || undefined}
              onClick={onSend}
            >
              {sending ? 'Sending…' : 'Send'}
              <SendHorizontal aria-hidden />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
